import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sidebar, MobileSidebar } from "@/components/ui/sidebar";
import { Wordmark } from "@/components/ui/wordmark";
import { GlobalSearch } from "@/components/layout/global-search";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/use-theme";

import { Bell, Menu, BarChart3, LogOut, User, Shield, Calendar, Sun, Moon, Monitor } from "lucide-react";
import { CannyChangelogButton } from "@/components/ui/canny-changelog";
const defaultLogo = "/logo-image.png";
import { NotificationBell } from "@/components/notifications/notification-bell";
import { BillingNudge } from "@/components/ui/billing-nudge";
import { FeedbackButton } from "@/components/feedback-button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { clearCsrfToken } from "@/lib/csrfClient";
import { useToast } from "@/hooks/use-toast";
import { getUserRoles } from "@/lib/authUtils";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { shutdownIntercom } from "@/lib/intercom";

// Version injected at build time by Vite
declare const __APP_VERSION__: string;
const APP_VERSION = __APP_VERSION__;

interface AppLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed';

export default function AppLayout({ children }: AppLayoutProps) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Initialize sidebar state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  // Persist sidebar state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(sidebarCollapsed));
    } catch (error) {
      console.error('Failed to save sidebar state:', error);
    }
  }, [sidebarCollapsed]);
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  const { theme, setTheme } = useTheme();

  // Get current organization for branding
  const { data: currentOrganization } = useQuery<any>({
    queryKey: ["/api/organizations/current"],
    queryFn: () => apiRequest("GET", "/api/organizations/current"),
  });

  // Overdue QBR count logic
  const { data: overdueQbrCount } = useQuery<any>({
    queryKey: ["/api/qbrs/overdue-count"],
    queryFn: () => apiRequest("GET", "/api/qbrs/overdue-count"),
    initialData: 0, // Default to 0 if not yet fetched
    select: (data) => {
      // Filter for QBRs that are scheduled past the current date and time
      return data?.filter((qbr: any) => qbr.status === "Scheduled" && new Date(qbr.dateTime) < new Date()).length || 0;
    },
    enabled: !!user, // Only fetch if user is logged in
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/auth/logout");
    },
    onSuccess: () => {
      // Shutdown Intercom to clear user identity and chat history
      shutdownIntercom();
      
      clearCsrfToken();
      queryClient.clear();
      window.location.href = "/auth";
    },
    onError: () => {
      toast({
        title: "Logout Failed",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  const getInitials = (firstName?: string, lastName?: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    return "U";
  };

  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    return user?.email || "User";
  };

  const getRoleDisplayName = (user: any) => {
    const roles = getUserRoles(user);
    if (roles.length === 0) return "User";
    if (roles.length === 1) {
      switch (roles[0]) {
        case "owner":
          return "Owner";
        case "alignment_engineer":
          return "Alignment Engineer";
        case "vcio":
          return "vCIO";
        case "admin":
          return "Administrator";
        default:
          return "User";
      }
    }
    // Multiple roles - show primary role or most important one (owner is highest)
    if (roles.includes("owner")) return "Owner";
    if (roles.includes("admin")) return "Administrator";
    if (roles.includes("vcio")) return "vCIO";
    if (roles.includes("alignment_engineer")) return "Alignment Engineer";
    return "User";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Fixed Hamburger Menu Button - Desktop */}
      {!isMobile && (
        <div className="fixed top-5 left-3 z-50">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="bg-background border-border shadow-md hover:shadow-lg transition-shadow"
            data-sidebar-toggle
          >
            <Menu className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Top Navigation */}
      <nav className="bg-card shadow-sm border-b border-border fixed w-full top-0 z-40">
        <div className="max-w-full px-0 py-2">
          <div className="relative flex items-center justify-between px-3 md:px-4">
            <div className="flex items-center space-x-2 md:space-x-3">
              {/* Mobile menu button (only on mobile) */}
              {isMobile && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMobileMenuOpen(true)}
                  className="h-8 w-8 p-0 flex items-center justify-center"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              )}
              {/* Organization branding - add left margin on desktop to avoid hamburger button */}
              <div
                className={cn(
                  "flex items-center gap-2 md:gap-3 cursor-pointer hover:opacity-80 transition-opacity",
                  !isMobile && "md:ml-12",
                )}
                data-onboarding="organization-display"
                onClick={() => setLocation("/")}
              >
                {currentOrganization?.settings?.logo ? (
                  <img
                    src={currentOrganization.settings.logo}
                    alt="Organization Logo"
                    className="w-8 h-8 md:w-10 md:h-10 object-contain flex-shrink-0"
                  />
                ) : (
                  <img
                    src={defaultLogo}
                    alt="TechAlign"
                    className="w-8 h-8 md:w-10 md:h-10 object-contain flex-shrink-0"
                  />
                )}
                <div className="flex flex-col">
                  <h1 className="text-base md:text-lg leading-tight">
                    <Wordmark
                      name={
                        currentOrganization?.settings?.brandName ||
                        currentOrganization?.name
                      }
                      tone="theme"
                    />
                  </h1>
                  <p className="text-xs text-muted-foreground leading-tight">
                    Powered by <Wordmark tone="theme" className="text-xs" />
                  </p>
                </div>
              </div>
              {/* Trial Status Indicator */}
              {currentOrganization?.subscriptionStatus === "trialing" && (
                <div
                  className="bg-orange-100 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg px-3 py-1 ml-4 hidden md:flex items-center gap-2 cursor-pointer hover:bg-orange-200 dark:hover:bg-orange-900/30 transition-colors"
                  onClick={() => setLocation("/billing")}
                >
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                    Trial:{" "}
                    {currentOrganization?.createdAt
                      ? Math.max(
                          0,
                          Math.ceil(
                            (new Date(currentOrganization.createdAt).getTime() +
                              14 * 24 * 60 * 60 * 1000 -
                              new Date().getTime()) /
                              (1000 * 60 * 60 * 24),
                          ),
                        )
                      : 0}{" "}
                    days left
                  </span>
                </div>
              )}
            </div>
            {/* Global Search - absolutely centered on desktop so it stays
                centered regardless of how wide the side zones are */}
            {!isMobile && (
              <div className="pointer-events-none absolute left-1/2 top-1/2 hidden w-full max-w-xl -translate-x-1/2 -translate-y-1/2 px-4 md:block">
                <div className="pointer-events-auto">
                  <GlobalSearch />
                </div>
              </div>
            )}
            <div className="flex items-center space-x-2 md:space-x-3">
              {/* Global Search - icon trigger on mobile */}
              {isMobile && <GlobalSearch />}
              {/* Calendar Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setLocation('/calendar')}
                className="h-8 w-8"
                title="Calendar"
              >
                <Calendar className="h-4 w-4" />
              </Button>

              {/* Notification Bell */}
              <NotificationBell />

              {/* Changelog Widget */}
              <CannyChangelogButton position="bottom" align="right" />

              {/* Overdue QBR Counter */}
              {overdueQbrCount > 0 && (
                <div className="relative" data-onboarding="overdue-qbrs">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setLocation("/qbrs?filter=overdue")}
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 h-8 w-8 relative"
                    title={`${overdueQbrCount} Overdue QBRs`}
                  >
                    <BarChart3 className="h-5 w-5" />
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs">
                      {overdueQbrCount}
                    </span>
                  </Button>
                </div>
              )}

              {/* Feedback Button */}
              <FeedbackButton />

              {/* Platform Management Button for Superadmins */}
              {user?.isSuperadmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLocation("/admin")}
                  className="platform-management-btn flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-900/30"
                >
                  <Shield className="platform-icon h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-yellow-700 dark:text-yellow-300 hidden sm:inline">
                    Platform Management
                  </span>
                  <span className="text-yellow-700 dark:text-yellow-300 sm:hidden">
                    Admin
                  </span>
                </Button>
              )}

              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center space-x-2 md:space-x-3 h-auto p-2"
                  >
                    <Avatar className="w-7 h-7 md:w-8 md:h-8">
                      <AvatarImage src={user?.profileImageUrl ?? undefined} />
                      <AvatarFallback className="bg-muted text-muted-foreground text-xs md:text-sm">
                        {getInitials(user?.firstName ?? undefined, user?.lastName ?? undefined)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-foreground">
                        {getUserDisplayName()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {getRoleDisplayName(user)}
                      </p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <div className="flex items-center space-x-3 p-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={user?.profileImageUrl ?? undefined} />
                      <AvatarFallback className="bg-muted text-muted-foreground text-sm">
                        {getInitials(user?.firstName ?? undefined, user?.lastName ?? undefined)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col space-y-0.5 min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {getUserDisplayName()}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setLocation("/profile")} className="justify-center">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal text-center py-1">
                    Theme
                  </DropdownMenuLabel>
                  <div className="flex gap-1 px-2 py-1">
                    <Button
                      variant={theme === "light" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTheme("light")}
                      className="flex-1 h-8 gap-1.5"
                    >
                      <Sun className="h-3.5 w-3.5" />
                      <span className="text-xs">Light</span>
                    </Button>
                    <Button
                      variant={theme === "slate" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTheme("slate")}
                      className="flex-1 h-8 gap-1.5"
                    >
                      <Monitor className="h-3.5 w-3.5" />
                      <span className="text-xs">Slate</span>
                    </Button>
                    <Button
                      variant={theme === "dark" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTheme("dark")}
                      className="flex-1 h-8 gap-1.5"
                    >
                      <Moon className="h-3.5 w-3.5" />
                      <span className="text-xs">Dark</span>
                    </Button>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => logoutMutation.mutate()}
                    disabled={logoutMutation.isPending}
                    className="justify-center"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>
                      {logoutMutation.isPending ? "Logging out..." : "Log out"}
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-center">
                    <span className="text-xs text-muted-foreground">
                      Version {APP_VERSION}
                    </span>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex pt-[65px]">
        {/* Desktop Sidebar - always render on desktop */}
        <div className="hidden md:block">
          <Sidebar
            collapsed={sidebarCollapsed}
            onCollapsedChange={setSidebarCollapsed}
          />
        </div>

        {/* Mobile Sidebar */}
        {isMobile && (
          <MobileSidebar
            open={mobileMenuOpen}
            onOpenChange={setMobileMenuOpen}
          />
        )}

        {/* Main Content */}
        <main
          className={cn(
            "flex-1 min-h-screen transition-all duration-300",
            !isMobile && (sidebarCollapsed ? "md:ml-16" : "md:ml-64"),
          )}
          data-onboarding-main="true"
        >
          <div className="px-3 md:px-4 lg:px-6 pt-4 pb-4 md:py-3 lg:py-4">
            {/* Billing Warning - only renders when needed */}
            <BillingNudge user={user} organization={currentOrganization} />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}